import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { User, CreateUserDto, UpdateUserDto, UserRole } from '@/types'
import { UserFormModal } from './UserFormModal'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import { agGridLocaleFr } from '@/config/ag-grid-locale-fr'
import { AGTableWrapper } from '@/components/ag-grid/AGTableWrapper'
import { getUsersColumnDefs } from '@/column-defs/users.column-defs'
import { toastError, toastSuccess } from '@/utils/toast'
import { usersService } from '@/services/users.service'
import { buildImpersonationUrl } from '@/utils/impersonation'
import { useAuth } from '@/hooks/useAuth'

interface ActorUsersPageProps {
  title: string
  createButtonLabel: string
  gridCollection: string
  users: User[] | null
  fetchUsers: () => Promise<void>
  onCreate: (data: CreateUserDto) => Promise<User>
  onUpdate: (id: string, data: UpdateUserDto) => Promise<User>
  onDelete: (id: string) => Promise<void>
  canManage: boolean
  fixedRole?: UserRole
  allowedRoles?: UserRole[]
  hideRoleColumn?: boolean
  createTitle?: string
  editTitle?: string
  autoGeneratePassword?: boolean
  showWholesalerColumn?: boolean
  requireBillingFields?: boolean
  showBrandingFields?: boolean
  showActorColumns?: boolean
  extraColumns?: ColDef[]
  showLoginAs?: boolean
}

export const ActorUsersPage: React.FC<ActorUsersPageProps> = ({
  title,
  createButtonLabel,
  gridCollection,
  users,
  fetchUsers,
  onCreate,
  onUpdate,
  onDelete,
  canManage,
  fixedRole,
  allowedRoles,
  hideRoleColumn = false,
  createTitle,
  editTitle,
  autoGeneratePassword = false,
  showWholesalerColumn = false,
  requireBillingFields = false,
  showBrandingFields = false,
  showActorColumns = false,
  extraColumns = [],
  showLoginAs = false,
}) => {
  const { user: currentUser, isAdmin, isImpersonating } = useAuth()
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [deleteUserId, setDeleteUserId] = useState<string | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const hasFetchedRef = useRef(false)

  useEffect(() => {
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true
    fetchUsers().catch(error => {
      console.error('Error loading users:', error)
    })
  }, [fetchUsers])

  const handleCreate = () => {
    setSelectedUser(null)
    setIsFormOpen(true)
  }

  const handleEdit = useCallback((user: User) => {
    setSelectedUser(user)
    setIsFormOpen(true)
  }, [])

  const handleDeleteClick = useCallback((id: string) => {
    setDeleteUserId(id)
  }, [])

  const canShowLoginAs = showLoginAs && isAdmin && !isImpersonating
  const handleLoginAs = useCallback(async (user: User) => {
    try {
      const { access_token } = await usersService.impersonate(user._id)
      window.open(buildImpersonationUrl(access_token), '_blank', 'noopener,noreferrer')
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erreur lors de la connexion en tant qu'utilisateur"
      toastError(errorMessage)
    }
  }, [])

  const handleDeleteConfirm = async () => {
    if (!deleteUserId) return

    setIsDeleting(true)
    try {
      await onDelete(deleteUserId)
      setDeleteUserId(null)
      toastSuccess('Utilisateur archivé avec succès')
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Erreur lors de l'archivage de l'utilisateur"
      toastError(errorMessage)
    } finally {
      setIsDeleting(false)
    }
  }

  const columnDefs: ColDef[] = useMemo(
    () =>
      getUsersColumnDefs({
        handleEdit,
        handleDeleteClick,
        handleLoginAs,
        currentUserId: currentUser?._id || null,
        canEdit: canManage,
        showLoginAs: canShowLoginAs,
        hideRoleColumn,
        showWholesalerColumn,
        showActorColumns,
        extraColumns,
      }),
    [
      handleEdit,
      handleDeleteClick,
      handleLoginAs,
      currentUser?._id,
      canManage,
      canShowLoginAs,
      hideRoleColumn,
      showWholesalerColumn,
      showActorColumns,
      extraColumns,
    ]
  )

  const defaultColDef: ColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,
      resizable: true,
      flex: 1,
      minWidth: 100,
    }),
    []
  )

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-semibold text-[var(--color-text-primary)]">{title}</h2>
        {canManage && <Button onClick={handleCreate}>{createButtonLabel}</Button>}
      </div>

      <div className="bg-white rounded-lg border border-[var(--color-border)] shadow-sm overflow-hidden">
        <div
          className="ag-theme-alpine"
          style={{ height: '600px', width: '100%', display: 'flex', flexDirection: 'column' }}
        >
          <AGTableWrapper collection={gridCollection}>
            <AgGridReact
              theme="legacy"
              rowData={users}
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              pagination={true}
              paginationPageSize={20}
              paginationPageSizeSelector={[10, 20, 50, 100]}
              animateRows={true}
              enableCellTextSelection={true}
              ensureDomOrder={true}
              suppressCellFocus={false}
              headerHeight={48}
              rowHeight={48}
              localeText={agGridLocaleFr}
            />
          </AGTableWrapper>
        </div>
      </div>

      {isFormOpen && (
        <UserFormModal
          user={selectedUser}
          onClose={() => {
            setIsFormOpen(false)
            setSelectedUser(null)
          }}
          onSuccess={() => {
            setIsFormOpen(false)
            setSelectedUser(null)
          }}
          fixedRole={fixedRole}
          allowedRoles={allowedRoles}
          onCreate={onCreate}
          onUpdate={onUpdate}
          createTitle={createTitle}
          editTitle={editTitle}
          autoGeneratePassword={autoGeneratePassword}
          requireBillingFields={requireBillingFields}
          showBrandingFields={showBrandingFields}
        />
      )}

      {deleteUserId && (
        <Modal
          title="Archiver l'utilisateur"
          isDanger={true}
          confirmButtonText="Archiver"
          onCancel={() => setDeleteUserId(null)}
          onConfirm={handleDeleteConfirm}
          isLoading={isDeleting}
          size="sm"
        >
          <p className="text-[var(--color-text-primary)]">
            Êtes-vous sûr de vouloir archiver cet utilisateur ? Il ne pourra plus se connecter.
          </p>
        </Modal>
      )}
    </div>
  )
}
